'use client';

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import type { MediaclipBeautyshotPhoto } from 'shared/types/mediaclip/actions/CreateMediaclipBeautyshot';
import { Product } from 'shared/types/product';
import Link from 'components/commercetools-ui/atoms/link';
import { useNavigationDots } from 'components/commercetools-ui/atoms/navigation-dots/useNavigationDots';
import Slider from 'components/commercetools-ui/atoms/slider';
import Breadcrumb from 'components/commercetools-ui/molecules/breadcrumb';
import PersonalizedTile from 'components/commercetools-ui/organisms/personalized-banner/Tile';
import { AccountContext } from 'context/account';
import { mapProductToGA4Item, trackCtaClick, trackViewItemList } from 'helpers/analytics/ga4Events';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { setupImageOptions } from 'helpers/imageData';
import { transformPhotosForMediaclipBeautyShots } from 'helpers/mediaclip/mediaclip-hub';
import { getMediaclipToken } from 'helpers/mediaclip/mediaclipToken';
import { getAttributeValueLowerCase } from 'helpers/utils/attributes';
import { getCustomizationData } from 'helpers/utils/customizationStorage';
import { desktop, mediumDesktop, mobile, tablet } from 'helpers/utils/screensizes';
import { useRouter } from 'i18n/routing';
import { useFeatures } from 'providers/feature-flag';
import { usePageLoader } from 'providers/loader';
import { sdk } from 'sdk';
import { Category as CategoryFromEntity } from 'types/entity/category';
import { useCart, useProduct, useProjectSettings } from 'frontastic';
import { setFFIDsForPagination } from '../curated-slider/helpers';
import { useUserName } from '../utils';

const PAGE_SIZE = 20;

const BEAUTYSHOT_TIMEOUT_MS = 3500;

const loadImageAvailability = (url: string) =>
  new Promise<boolean>((resolve) => {
    if (!url) return resolve(false);
    const ImageCtor = (globalThis as any).Image;
    if (!ImageCtor) return resolve(false);

    const img = new ImageCtor();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });

const buildBeautyshotItem = (
  item: any,
  payloadPhoto: MediaclipBeautyshotPhoto[],
  mediaClipProjectIdDataSource: string,
) => ({
  productId: item.productId,
  module: item.module,
  themeUrl: item.themeUrl,
  ...(item.source === 'configured'
    ? {
      photos: payloadPhoto,
    }
    : {
      projectId: mediaClipProjectIdDataSource,
    }),
});

const withTimeout = async <T,>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
  return await Promise.race([
    promise,
    new Promise<T>((_, reject) => setTimeout(() => reject(new Error('BeautyshotTimeout')), timeoutMs)),
  ]);
};

const getVariantAttrs = (p: any) =>
  p?.variant?.attributes ??
  p?.variants?.[0]?.attributes ??
  p?.variants?.[0]?.variant?.attributes ??
  p?.attributes ??
  {};

const isMediaclip = (p: any) => {
  const attrs = getVariantAttrs(p);

  return !!(attrs?.mediaclip_product_id_universal || attrs?.mediaclip_theme_url_universal);
};

const getAgencyId = (p: any) => {
  const attrs = getVariantAttrs(p);
  return attrs?.agency_design_id_universal;
};

const getSku = (p: any) => p?.variant?.sku || p?.variants?.[0]?.sku || p?.sku || p?.productKey || p?.productId || null;

const dedupeBySku = <T,>(items: T[]) => {
  const seen = new Set<string>();
  const out: T[] = [];

  for (const item of items) {
    const key = getSku(item) ?? `__nosku__${(item as any)?.productKey ?? (item as any)?.productId ?? Math.random()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
};

const getSlideAnnouncement = (
  template: string | undefined,
  start: number,
  end: number,
  total: number
) => {
  const defaultTemplate = 'Showing items {start} through {end} of {total}';

  const announcementTemplate = template?.trim() || defaultTemplate;

  return announcementTemplate
    .replace('{start}', String(start))
    .replace('{end}', String(end))
    .replace('{total}', String(total));
};

export default function JustForYou({ onSuccess, onError, personalizedCartBannerData, ...data }: any) {
  const {
    headerText,
    headerTextAlign = 'center',
    headerFontSize = 40,
    headerFontColor = '#111111',
    headerFontStyle = 'normal',
    subHeaderTextAlign = 'center',
    subHeaderFontSize = 28,
    subHeaderFontColor = '#858585',
    subHeaderFontStyle = 'normal',
    backgroundColor = '#FFF',
    backgroundImage,
    backgroundSize = 'cover',
    backgroundRepeat = 'no-repeat',
    previewLabel,
    saveToProjects,
    loadMore,
    noPersonalized,
    browseProducts,
    browseProductsLink,
    breadCrumbLevel1,
    breadCrumbLevel2,
    subHeaderText,
    tiles,
    beautyShotBatchCall,
    tileBgColor,
    tileImgContainerBgColor,
    mainHeadingWithoutUserName,
    ariaLabelOfPreviousButton,
    ariaLabelOfNextButton,
    autoRotation,
    autoRotationSpeed,
    mainHeadingPosition,
    tileCtaText,
    subHeadingWithoutName,
    showBreadcrumb,
    ctaText,
    slideChangeAnnouncement,
  } = data;

  const [isMobileSize, width] = useMediaQuery(mobile);
  const isPureMobile = width < tablet;
  const [isDesktopSize] = useMediaQuery(desktop);
  const { projectSettings } = useProjectSettings();
  const imageOptions = useMemo(() => setupImageOptions(projectSettings), [projectSettings]);
  const { showLoader, hideLoader } = usePageLoader();

  const router = useRouter();
  const userName = useUserName();

  const [isTabletSize] = useMediaQuery(tablet);

  const [visibleCount, setVisibleCount] = useState<number>(PAGE_SIZE);
  const [tilesLoaded, setTilesLoaded] = useState<boolean>(!tiles?.length);

  const beautyshotBatchIndexRef = useRef(0);
  const beautyshotIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const products: Product[] = useMemo(
    () => personalizedCartBannerData?.items || [],
    [personalizedCartBannerData?.items],
  );
  const mediaClipProjectIdDataSource = personalizedCartBannerData?.mediaClipProjectId as string;

  const productsById = useMemo(() => {
    const m = new Map<string, Product>();
    for (const p of products) {
      if (p?.productId) m.set(String(p.productId), p);
    }
    return m;
  }, [products]);

  const { account, accountLoaded } = useContext(AccountContext);
  const { orderHistory } = useCart();
  const [hasOrders, setHasOrders] = useState(false);
  const [hasMediaclipOrders, setHasMediaclipOrders] = useState(false);
  const [pastOrderProjectId, setPastOrderProjectId] = useState<string>('');
  const [purchasedAgencyIds, setPurchasedAgencyIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!accountLoaded) {
      return;
    }

    if (!account?.accountId) {
      setHasOrders(false);
      setHasMediaclipOrders(false);
      setPastOrderProjectId('');
      setPurchasedAgencyIds(new Set());
      return;
    }

    let cancelled = false;

    const loadOrderHistory = async () => {
      try {
        const orders = await orderHistory?.();
        if (cancelled) return;

        const activeOrders = (orders ?? []).filter((o: any) => o?.orderState !== 'Cancelled');
        setHasOrders(activeOrders.length > 0);

        if (!activeOrders.length) {
          setHasMediaclipOrders(false);
          setPastOrderProjectId('');
          setPurchasedAgencyIds(new Set());
          return;
        }

        let foundMediaclip = false;
        let sourceProjectId = '';
        const agencySet = new Set<string>();

        for (const order of activeOrders) {
          for (const item of order?.lineItems ?? []) {
            const attrs = item?.variant?.attributes;
            const custom = item?.custom?.fields;

            const mediaclipMarker =
              attrs?.mediaclip_product_id_universal ||
              custom?.mediaclip_product_id ||
              custom?.mediaclipProductId ||
              custom?.mediaClipProjectId;

            const agencyId = attrs?.agency_design_id_universal || custom?.agency_design_id || custom?.agencyDesignId;

            if (mediaclipMarker) foundMediaclip = true;
            if (!sourceProjectId && custom?.mediaClipProjectId) {
              sourceProjectId = String(custom.mediaClipProjectId);
            }
            if (mediaclipMarker && agencyId) agencySet.add(String(agencyId));

            if (!agencyId && item?.productId) {
              const p = productsById.get(String(item.productId));
              if (p && isMediaclip(p)) {
                foundMediaclip = true;
                const aid = getAgencyId(p);
                if (aid) agencySet.add(String(aid));
              }
            }
          }
        }

        setHasMediaclipOrders(foundMediaclip);
        setPastOrderProjectId(foundMediaclip ? sourceProjectId : '');
        setPurchasedAgencyIds(agencySet);
      } catch (e) {
        console.error('loadOrderHistory failed:', e);
        setHasOrders(false);
        setHasMediaclipOrders(false);
        setPastOrderProjectId('');
        setPurchasedAgencyIds(new Set());
      }
    };

    void loadOrderHistory();
    return () => {
      cancelled = true;
    };
  }, [accountLoaded, account?.accountId, orderHistory, productsById]);

  const { query } = useProduct();
  const [tileProducts, setTileProducts] = useState<Product[]>([]);

  useEffect(() => {
    if (!tiles?.length) {
      setTilesLoaded(true);
      return;
    }

    let cancelled = false;
    const fetchTileProducts = async () => {
      setTilesLoaded(false);
      try {
        const productKeys = (tiles ?? [])
          .map((t: any) => t?.productKey)
          .filter((k: unknown): k is string => typeof k === 'string' && k.trim().length > 0);

        const uniqueKeys = Array.from(new Set(productKeys)) as string[];
        if (!uniqueKeys.length) {
          if (!cancelled) setTileProducts([]);
          return;
        }

        const response: any = await query({
          productKeys: uniqueKeys,
        });

        const items = response?.isError || !response?.data?.items ? [] : response.data.items;

        if (!cancelled) {
          setTileProducts(items);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) setTileProducts([]);
      } finally {
        if (!cancelled) setTilesLoaded(true);
      }
    };

    void fetchTileProducts();

    return () => {
      cancelled = true;
    };
  }, [query, tiles]);

  const productMap = useMemo<Record<string, any>>(() => {
    const map: Record<string, any> = {};
    tileProducts.forEach((p: any) => {
      if (p.productKey) map[p.productKey] = { ...p, variant: p.variants?.[0] };
      p.variants?.forEach((variant: any) => {
        if (variant?.sku) map[variant.sku] = { ...p, variant };
      });
    });
    return map;
  }, [tileProducts]);

  const configuredList = useMemo(() => {
    if (!tiles?.length) return [];

    return (tiles ?? [])
      .map((tile: any) => {
        const lookupKey = tile?.productKey ?? tile?.sku;
        const matched = lookupKey ? productMap[lookupKey] : undefined;
        if (!matched) {
          return null;
        }

        if (matched && !isMediaclip(matched)) return null;

        const merged = {
          ...matched,
          ...tile,
          attributes: matched?.attributes ?? tile?.attributes ?? {},
          variant: matched.variant ?? matched.variants?.[0],
          variants: matched.variants,
          source: 'configured',
        };

        return merged;
      })
      .filter(Boolean) as any[];
  }, [tiles, productMap]);

  const similarList = useMemo(() => {
    if (!hasMediaclipOrders) return [];
    if (!purchasedAgencyIds.size) return [];

    return (products ?? []).filter(isMediaclip).filter((p: any) => {
      const aid = getAgencyId(p);
      return aid && purchasedAgencyIds.has(String(aid));
    });
  }, [products, hasMediaclipOrders, purchasedAgencyIds]);

  const personalizedMediaclipList = useMemo(() => {
    return dedupeBySku(
      (products ?? []).filter(isMediaclip).map((p: any) => ({
        ...p,
        source: 'mediaclip',
      })),
    );
  }, [products]);

  const renderList = useMemo(() => {
    const mediaclipWhenReady = tilesLoaded ? personalizedMediaclipList : [];

    const combinedBase = dedupeBySku([...configuredList, ...mediaclipWhenReady]);

    if (!combinedBase.length) return [];
    if (!hasOrders || (hasOrders && !hasMediaclipOrders)) {
      return combinedBase;
    }
    return dedupeBySku([...combinedBase, ...similarList]);
  }, [configuredList, personalizedMediaclipList, tilesLoaded, hasOrders, hasMediaclipOrders, similarList]);

  useEffect(() => {
    setVisibleCount(Math.min(PAGE_SIZE, renderList.length));
  }, [renderList.length]);

  const [beautyshots, setBeautyshots] = useState<Record<string, string>>({});

  const beautyshotRequestKey = useMemo(() => {
    if (!renderList?.length) return null;
    const stringCompare = (a: string, b: string) => a?.localeCompare(b);
    return JSON.stringify({
      items: renderList
        ?.map((p: any) => p?.productKey ?? p?.productId)
        ?.filter(Boolean)
        ?.sort(stringCompare),
    });
  }, [renderList]);

  useEffect(() => {
    if (!accountLoaded) return;
    if (!beautyshotRequestKey || !renderList.length) {
      return;
    }

    beautyshotBatchIndexRef.current = 0;

    if (beautyshotIntervalRef.current) {
      clearInterval(beautyshotIntervalRef.current);
      beautyshotIntervalRef.current = null;
    }

    let cancelled = false;

    const run = async () => {
      let photoInfos = getCustomizationData()?.photoInfos ?? [];
      if (!photoInfos.length) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        photoInfos = getCustomizationData()?.photoInfos ?? [];
      }
      const payloadPhoto = transformPhotosForMediaclipBeautyShots(photoInfos.slice(0, 8), imageOptions);
      try {
        const batchSize = Number(beautyShotBatchCall) || 10;
        const start = beautyshotBatchIndexRef.current * batchSize;
        const end = start + batchSize;

        const batchList = renderList.slice(start, end);

        if (!batchList.length) {
          if (beautyshotIntervalRef.current) {
            clearInterval(beautyshotIntervalRef.current);
            beautyshotIntervalRef.current = null;
          }
          return;
        }

        const items = batchList
          .map((p: any) => {
            const attrs = {
              ...(p?.attributes || {}),
              ...(getVariantAttrs(p) || {}),
            };

            if (!attrs?.mediaclip_product_id_universal && !attrs?.mediaclip_theme_url_universal) {
              return null;
            }

            const orientation = getAttributeValueLowerCase(attrs, 'orientation');

            let orientationValue = '';

            if (orientation === 'both' || orientation === 'landscape' || orientation === 'horizontal') {
              orientationValue = `${attrs?.mediaclip_product_id_universal}-L`;
            } else if (orientation === 'portrait' || orientation === 'vertical') {
              orientationValue = `${attrs?.mediaclip_product_id_universal}-P`;
            } else {
              orientationValue = `${attrs?.mediaclip_product_id_universal}`;
            }

            return {
              key: p.productKey ?? p.productId ?? '',
              productId: orientationValue,
              module: attrs.mediaclip_module_universal,
              themeUrl:
                p?.attributes?.mediaclip_theme_url_universal ||
                attrs?.mediaclip_theme_url_universal ||
                '$(package:mediaclip/photobooks)/themes/default',
              source: p.source,
            };
          })
          .filter((item) => {
            if (!item) return false;

            if (item.source === 'configured' && (!payloadPhoto || payloadPhoto.length === 0)) {
              return false;
            }

            return true;
          }) as any[];

        if (!items.length) return;

        // Mediaclip rejects beautyshots for users with no minted token, so wait for one.
        const token = await getMediaclipToken();
        if (!token) return;

        const response: any = await withTimeout(
          sdk.composableCommerce.mediaClip.getBatchBeautyshots({
            beautyshots: items.map((item) => buildBeautyshotItem(item, payloadPhoto, mediaClipProjectIdDataSource)),
          }),
          BEAUTYSHOT_TIMEOUT_MS,
        );

        if (response.isError || !Array.isArray(response.data?.beautyshotsUrls)) return;

        const urls: string[] = response.data.beautyshotsUrls;
        const availability = await Promise.all(urls.map(loadImageAvailability));

        const shots: Record<string, string> = {};
        items.forEach((it, i) => {
          if (!cancelled && availability[i] && it.key) {
            shots[it.key] = urls[i];
          }
        });

        if (!cancelled && Object.keys(shots).length) {
          setBeautyshots((prev) => ({ ...prev, ...shots }));
        }
      } catch (e) {
        console.warn('Beautyshot generation failed/timed out:', e);
      }
    };

    void run();

    if (!beautyshotIntervalRef.current) {
      beautyshotIntervalRef.current = setInterval(() => {
        beautyshotBatchIndexRef.current += 1;
        void run();
      }, 60000);
    }
    return () => {
      cancelled = true;

      if (beautyshotIntervalRef.current) {
        clearInterval(beautyshotIntervalRef.current);
        beautyshotIntervalRef.current = null;
      }
    };
  }, [beautyshotRequestKey, renderList, beautyShotBatchCall, imageOptions, mediaClipProjectIdDataSource]);

  const viewItemListFired = useRef(false);
  const { enableGA4 } = useFeatures();
  useEffect(() => {
    if (renderList.length > 0 && !viewItemListFired.current) {
      const listName = 'Just For You';
      const items = renderList.map((p: any, index: number) =>
        mapProductToGA4Item(enableGA4(), p, { index, itemListName: listName }),
      );
      trackViewItemList(listName, items);
      viewItemListFired.current = true;
    }
  }, [renderList, enableGA4]);

  const containerStyle: React.CSSProperties = {
    backgroundColor,
    backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
    backgroundSize,
    backgroundRepeat,
  };

  const homeBreadCrumb = {
    name: breadCrumbLevel1 || 'Home',
    categoryId: '_home',
    _url: `/`,
  } as CategoryFromEntity;

  const breadcrumbRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (tilesLoaded) return;
    breadcrumbRef.current?.focus();
  }, [tilesLoaded]);

  const isEnabled = isDesktopSize;
  const shouldShowArrows = (
    sectionClass: string | null | undefined,
    { isMobileSize, isTabletSize }: { isMobileSize: boolean; isTabletSize: boolean },
  ): boolean => {
    const sectionClassLower = (sectionClass ?? '').toLowerCase();
    if (sectionClassLower === 'product-recommend') return isTabletSize;
    if (['past-order', 'review-projects'].includes(sectionClassLower)) return isMobileSize && !isTabletSize;
    return false;
  };

  const hasRelatedProducts = (product: Product) =>
    Array.isArray(product?.variants?.[0]?.attributes?.related_products) &&
    product.variants[0].attributes.related_products.length > 0;

  const mediaclipProducts = useMemo(() => {
    const seenAgencyIds = new Set<string>();

    return products?.filter((product: Product) => {
      const attrs = product?.variants?.[0]?.attributes;

      if (!attrs?.mediaclip_product_id_universal) return false;

      if (hasRelatedProducts(product)) return false;

      const agencyId = getAgencyId(product);
      if (agencyId) {
        if (seenAgencyIds?.has(agencyId)) return false;
        seenAgencyIds?.add(agencyId);
      }

      return true;
    });
  }, [products]);

  const [limitedMediaclip, setLimitedMediaclip] = useState<Product[]>([]);

  useEffect(() => {
    if (mediaclipProducts.length > 0) {
      const mediaclipItems = mediaclipProducts.slice(0, 4);

      const configuredItems = configuredList
        .filter((configItem) => !mediaclipItems.some((mediaItem) => getSku(mediaItem) === getSku(configItem)))
        .slice(0, Math.max(0, 4 - mediaclipItems.length));

      setLimitedMediaclip(dedupeBySku([...mediaclipItems, ...configuredItems]));
    } else {
      setLimitedMediaclip([]);
    }
  }, [mediaclipProducts, configuredList]);

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const processedSubHeading = userName ? subHeaderText?.replace('{{firstName}}', userName) : subHeadingWithoutName;

  const [swiperInstance, setSwiperInstance] = React.useState<any>(null);
  const [slideDescription, setSlideDescription] = useState<string>('');
  const [isSliderFocused, setIsSliderFocused] = useState<boolean>(false);
  const [slideAnnouncement, setSlideAnnouncement] = useState('');

  useEffect(() => {
    if (successMessage || errorMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
        setErrorMessage(null);
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage]);

  const handleSliderContainerFocus = useCallback(() => {
    setIsSliderFocused(true);
  }, []);
  const { currentSlide } = useNavigationDots({
    swiper: swiperInstance,
    totalSlides: tiles?.length || 0,
    autoHide: false,
  });

  const handleSliderContainerBlur = useCallback((event: React.FocusEvent<HTMLElement>) => {
    const nextFocusedElement = event.relatedTarget as Node | null;
    const container = event.currentTarget;

    if (nextFocusedElement) {
      if (container.contains(nextFocusedElement)) {
        return;
      }

      setIsSliderFocused(false);
      return;
    }

    // Defer blur handling because autoplay slide transitions can momentarily blur
    // before focus is restored within the carousel. Capture `container` synchronously
    // above since React nulls out the pooled event's currentTarget before this runs.
    requestAnimationFrame(() => {
      const activeElement = document.activeElement;
      if (!container || (activeElement && container.contains(activeElement))) {
        return;
      }

      setIsSliderFocused(false);
    });
  }, []);

  useEffect(() => {
    if (!tiles?.length) return;

    const tile = tiles[currentSlide];
    const headline = tile?.tileTitle;
    const subtext = tile?.productKey;

    setSlideDescription(headline || subtext);
  }, [currentSlide, tiles]);

  const handleSlideChange = (swiper: any) => {
    const total = swiper.slides.length;
    const visibleCount = 2;

    const start = swiper.activeIndex + 1;
    const end = Math.min(start + visibleCount - 1, total);

    setSlideAnnouncement(
      getSlideAnnouncement(
        slideChangeAnnouncement,
        start,
        end,
        total
      )
    );
  };

  const breadcrumbs = (
    <div className="mx-0 sm:mx-24 md:mx-75 lg:mx-76 xl:mx-0">
      <div className="container px-16 sm:px-0">
        <Breadcrumb backCategory={homeBreadCrumb} listClassName="w-fit" ref={breadcrumbRef}>
          <Link link="/" className="truncate text-14" data-ffid="lnk-breadcrumb-fav-home" ref={breadcrumbRef}>
            <p title="Home" data-ffid="lbl-breadcrumb-fav-home" className="text-14">
              {breadCrumbLevel1}
            </p>
          </Link>

          <p
            className="max-w-170 cursor-default truncate text-14 font-bold lg:max-w-250"
            title="Search Results"
            data-ffid="lbl-breadcrumb-fav"
          >
            {isDesktopSize ? breadCrumbLevel2 : ''}
          </p>
        </Breadcrumb>
      </div>
    </div>
  );

  if (tiles?.length && !tilesLoaded) {
    return (
      <>
        {showBreadcrumb && breadcrumbs}
        {showLoader()}
      </>
    );
  }

  if (renderList.length === 0 && showBreadcrumb) {
    hideLoader();
    return (
      <>
        {showBreadcrumb && breadcrumbs}
        <div className="flex flex-col items-center py-60 text-center">
          <p>{noPersonalized ?? 'No personalized products available yet.'}</p>
          <button
            type="button"
            className="mt-24 rounded-full border border-[#00857A] px-24 py-10 text-[#00857A]"
            onClick={() => router.push(browseProductsLink ?? '/')}
          >
            {browseProducts}
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      {hideLoader()}
      {showBreadcrumb && breadcrumbs}
      <div
        className="mx-0 py-20 sm:mx-24 md:mx-75 lg:mx-76 xl:mx-0"
        style={containerStyle}
        data-testid="personalized-banner-container"
        data-ffid="ctr-personalized-banner"
      >
        <div className="curated-slider product-recommend container relative px-16 sm:px-0">
          <div className="mb-36 flex flex-col items-center">
            <h2
              className="w-full whitespace-normal break-words"
              style={{
                color: headerFontColor,
                textAlign: headerTextAlign,
                fontSize: isPureMobile ? '24px' : `${headerFontSize}px`,
                fontStyle: headerFontStyle,
              }}
              data-testid="personalized-banner-header"
              data-ffid="lbl-personalized-banner-header"
            >
              {userName ? `${userName}, ${headerText}` : mainHeadingWithoutUserName}
            </h2>

            <p
              className="mt-12 w-full max-w-[50rem] whitespace-normal break-words leading-[1.5]"
              style={{
                color: subHeaderFontColor,
                textAlign: subHeaderTextAlign,
                fontSize: isPureMobile ? '16px' : `${subHeaderFontSize}px`,
                fontStyle: subHeaderFontStyle,
              }}
              data-testid="personalized-banner-subheader"
              data-ffid="lbl-personalized-banner-subheader"
            >
              {processedSubHeading}
            </p>
          </div>

          {showBreadcrumb ? (
            <>
              {isDesktopSize ? (
                <div className="grid grid-cols-2 gap-12">
                  {renderList.slice(0, visibleCount).map((item: any, index: number) => {
                    const key = item?.productKey ?? item?.productId ?? `${index}`;
                    return (
                      <div key={`${key}-${index}`} className="w-full">
                        <PersonalizedTile
                          sourceProjectId={hasMediaclipOrders ? pastOrderProjectId : ''}
                          product={item}
                          data={item}
                          keyprop={index}
                          previewLabel={previewLabel}
                          saveToProjects={saveToProjects}
                          beautyshot={beautyshots[item.productKey ?? item.productId ?? '']}
                          onSuccess={onSuccess}
                          onError={onError}
                          successMessageText={data?.successMessage as string}
                          errorMessageText={data?.errorMessage as string}
                          tileBgColor={tileBgColor}
                          tileImgContainerBgColor={tileImgContainerBgColor ?? '#FFF'}
                        />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <>
                  {renderList.slice(0, visibleCount).map((item: any, index: number) => {
                    const key = item?.productKey ?? item?.productId ?? `${index}`;
                    return (
                      <div key={`${key}-${index}`} className="w-full">
                        <PersonalizedTile
                          sourceProjectId={hasMediaclipOrders ? pastOrderProjectId : ''}
                          product={item}
                          data={item}
                          keyprop={index}
                          previewLabel={previewLabel}
                          saveToProjects={saveToProjects}
                          beautyshot={beautyshots[item.productKey ?? item.productId ?? '']}
                          onSuccess={onSuccess}
                          onError={onError}
                          successMessageText={data?.successMessage as string}
                          errorMessageText={data?.errorMessage as string}
                          tileBgColor={tileBgColor}
                          tileImgContainerBgColor={tileImgContainerBgColor ?? '#FFF'}
                        />
                      </div>
                    );
                  })}
                </>
              )}
              {visibleCount < renderList.length && (
                <div className="mt-24 flex justify-center">
                  <button
                    type="button"
                    className="rounded-full border border-[#00857A] px-32 py-12 text-[#00857A]"
                    onClick={() => setVisibleCount((v) => Math.min(v + PAGE_SIZE, renderList.length))}
                  >
                    {loadMore}
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              {!isDesktopSize ? (
                <>
                  {mediaclipProducts.length > 0
                    ? limitedMediaclip.map((item: Product, index: number) => (
                      <PersonalizedTile
                        sourceProjectId={hasMediaclipOrders ? pastOrderProjectId : ''}
                        key={`${item?.name}-${index}`}
                        keyprop={index}
                        product={item}
                        beautyshot={beautyshots[item.productKey ?? item.productId ?? '']}
                        previewLabel={previewLabel}
                        saveToProjects={saveToProjects}
                        onSuccess={(msg: string) => {
                          setSuccessMessage(msg);
                          setErrorMessage(null);
                        }}
                        onError={(msg: string) => {
                          setErrorMessage(msg);
                          setSuccessMessage(null);
                        }}
                        data={data}
                      />
                    ))
                    : tiles
                      ?.slice(0, 4)
                      ?.map((tile: any, index: any) => {
                        const productKey = tile.productKey as string;
                        const matchedProduct = productKey ? productMap[productKey] : undefined;
                        const beautyShot = beautyshots[productKey];

                        return (
                          <PersonalizedTile
                            sourceProjectId={hasMediaclipOrders ? pastOrderProjectId : ''}
                            key={tile}
                            product={matchedProduct}
                            data={tile}
                            keyprop={index}
                            previewLabel={previewLabel}
                            saveToProjects={saveToProjects}
                            beautyshot={beautyShot}
                            onSuccess={onSuccess}
                            onError={onError}
                            successMessageText={data?.successMessage as string}
                            errorMessageText={data?.errorMessage as string}
                            tileBgColor={tileBgColor}
                            tileImgContainerBgColor={tileImgContainerBgColor ?? '#FFF'}
                          />
                        );
                      })
                      .filter(Boolean)}
                </>
              ) : (
                <section
                  aria-label="Just For You"
                  aria-roledescription="carousel"
                  data-ffid="fra-just-for -you"
                  onFocus={handleSliderContainerFocus}
                  onBlur={handleSliderContainerBlur}
                >
                  <Slider
                    a11y={{
                      enabled: true,
                      prevSlideMessage: ariaLabelOfPreviousButton,
                      nextSlideMessage: ariaLabelOfNextButton,
                      containerRoleDescriptionMessage: '',
                      slideRole: 'presentation',
                      itemRoleDescriptionMessage: '',
                    }}
                    pagination={{
                      enabled: isEnabled,
                      clickable: true, // makes dots clickable
                      dynamicBullets: true, // larger active + neighbor bullets
                    }}
                    onInit={(swiper) => {
                      setSwiperInstance(swiper);
                      setFFIDsForPagination(swiper, 'personalizedprod');
                    }}
                    onSwiper={(swiper) => {
                      setSwiperInstance(swiper);
                    }}
                    slidesPerView={1.4}
                    centeredSlides={!isDesktopSize}
                    centeredSlidesBounds={!isDesktopSize}
                    onSlideChange={handleSlideChange}
                    aria-label={`${headerText?.trim() || ''}`}
                    uniqueFFIDText="personalizedprod"
                    dots={false}
                    spaceBetween={12}
                    arrows={shouldShowArrows('product-recommend', { isMobileSize, isTabletSize })}
                    arrowsClassName={clsx('z-20', 'bottom-30')}
                    ariaLabelOfPreviousButton={ariaLabelOfPreviousButton}
                    ariaLabelOfNextButton={ariaLabelOfNextButton}
                    allowTouchMove={true}
                    autoplay={
                      autoRotation && autoRotationSpeed
                        ? {
                          delay: autoRotationSpeed * 1000,
                          disableOnInteraction: true,
                          pauseOnMouseEnter: true,
                        }
                        : false
                    }
                    slidesOffsetAfter={0}
                    loop={false}
                    freeMode={false}
                    centerInsufficientSlides
                    breakpoints={{
                      [mobile]: {
                        spaceBetween: 16,
                        slidesPerView: 2.2,
                        slidesOffsetAfter: 0,
                        centeredSlides: false,
                        centeredSlidesBounds: false,
                      },
                      [desktop]: {
                        slidesOffsetAfter: 0,
                        slidesPerView: 2,
                      },
                      [mediumDesktop]: {
                        spaceBetween: 20,
                        slidesPerView: 2,
                      },
                    }}
                    navPosition={mainHeadingPosition === 'right' ? 'left' : 'right'}
                    slideDescription={isSliderFocused ? slideDescription : undefined}
                  >
                    {mediaclipProducts.length > 0
                      ? limitedMediaclip.map((item: Product, index: number) => (
                        <PersonalizedTile
                          sourceProjectId={hasMediaclipOrders ? pastOrderProjectId : ''}
                          key={`${item?.name}-${index}`}
                          keyprop={index}
                          product={item}
                          beautyshot={beautyshots[item.productKey ?? item.productId ?? '']}
                          previewLabel={tileCtaText}
                          saveToProjects="save to projects +"
                          onSuccess={(msg: string) => {
                            setSuccessMessage(msg);
                            setErrorMessage(null);
                          }}
                          onError={(msg: string) => {
                            setErrorMessage(msg);
                            setSuccessMessage(null);
                          }}
                          data={data}
                        />
                      ))
                      : tiles
                        ?.slice(0, 4)
                        ?.map((tile: any, index: any) => {
                          const productKey = tile.productKey as string;
                          const matchedProduct = productKey ? productMap[productKey] : undefined;
                          const beautyShot = beautyshots[productKey];

                          return (
                            <PersonalizedTile
                              sourceProjectId={hasMediaclipOrders ? pastOrderProjectId : ''}
                              key={tile}
                              product={matchedProduct}
                              data={tile}
                              keyprop={index}
                              previewLabel={previewLabel}
                              saveToProjects={saveToProjects}
                              beautyshot={beautyShot}
                              onSuccess={onSuccess}
                              onError={onError}
                              successMessageText={data?.successMessage as string}
                              errorMessageText={data?.errorMessage as string}
                              tileBgColor={tileBgColor}
                              tileImgContainerBgColor={tileImgContainerBgColor ?? '#FFF'}
                            />
                          );
                        })
                        .filter(Boolean)}
                  </Slider>
                  <div
                    className="sr-only"
                    aria-live="polite"
                    aria-atomic="true"
                  >
                    {slideAnnouncement}
                  </div>
                </section>
              )}
              <div className={`${!isDesktopSize ? 'mt-20' : 'mt-0'} pb-20" flex w-full items-center justify-between`}>
                <div className={`${!isDesktopSize ? 'w-full text-center' : 'flex-1'}`}>
                  <button
                    className="rounded-full border border-[#00857A] px-24 py-10 text-16 font-medium text-[#00857A]"
                    data-testid="view-more-ideas-button"
                    data-ffid="btn-view-more-ideas-pb"
                    type="button"
                    onClick={() => {
                      router.push('/just-for-you');
                      trackCtaClick('personalized_banner', ctaText?.[0]?.ctaText || 'View more ideas', '');
                    }}
                  >
                    {ctaText?.[0]?.ctaText}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
